import { ReactNode } from 'react';
import { IconProps } from '@amzn/awsui-components-core';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';
import {
  ActionPanelId,
  ActionPanelState,
} from '../components/v2-content-action-panel/ActionPanelTypes';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';

/** Descriptor for a single toolbar action button. */
export interface ToolbarAction {
  /** Unique identifier matching an ActionPanelId. Non-empty, max 64 chars. */
  readonly id: ActionPanelId;
  /** Cloudscape Icon name. Provide this OR `iconSvg`, not both. */
  readonly icon?: IconProps.Name;
  /** Custom SVG icon for glyphs not in the Cloudscape set. Provide this OR `icon`, not both. */
  readonly iconSvg?: ReactNode;
  /** Tooltip text. 1-128 chars. */
  readonly tooltip: string;
  /** Optional override for screen-reader label. 1-128 chars if provided. */
  readonly ariaLabel?: string;
  /** Sort position. Lower values appear first. */
  readonly sortOrder?: number;
  /** When true, the button renders greyed out and is not clickable. */
  readonly disabled?: boolean;
  readonly disabledReason?: string;
}

/** Returns a copy of the actions with every button marked disabled. */
export function disableToolbarActions(actions: readonly ToolbarAction[]): readonly ToolbarAction[] {
  return actions.map((action) => ({ ...action, disabled: true }));
}

/**
 * List of available "help" topics.
 *
 * To add/remove help topics, first, insert/remove topic ids in this list.
 * Then look up the code that references the `infoPaneTopicId` property below
 * and update them.
 */
const INFO_PANE_TOPIC_IDS = [
  'code-link',
  'intended-audience',
  'workload',
  'pattern-type',
  'environment',
  'source',
  'target',
  'rtype',
  'technologies',
  'content-request-creation',
  'content-href',
  'content-page-sections',
  'content-titles',
  'content-overview',
  'content-problem-solved',
  'content-proposal',
  'content-type',
  'pattern-summary',
  'pattern-architecture',
  'pattern-prerequisites',
  'pattern-tools',
  'pattern-epics',
  'pattern-troubleshooting',
  'pattern-general',
] as const;

/** Identifies a topic to display in the info pane. */
export type InfoPaneTopicId = (typeof INFO_PANE_TOPIC_IDS)[number];

const INFO_PANE_TOPIC_ID_SET: ReadonlySet<string> = new Set(INFO_PANE_TOPIC_IDS);

/** Runtime check that narrows a string to InfoPaneTopicId. */
export function isInfoPaneTopicId(value: string): value is InfoPaneTopicId {
  return INFO_PANE_TOPIC_ID_SET.has(value);
}

/** Click handler signature: receives the action ID that was clicked. */
export type ToolbarActionClickHandler = (actionId: ActionPanelId) => void;

/** Getter that returns the current active panel state. */
export type ActivePanelGetter = () => ActionPanelState;

export type ToolbarActionServiceEvents = {
  /** Fires when the registered actions change. */
  changed: () => void;
};

/**
 * Manages toolbar action buttons displayed in the application header.
 *
 * The service follows a registration-based API: a page
 * registers actions on mount and unregisters on unmount. The header
 * subscribes to change events and renders whatever actions are currently
 * registered.
 */
export class ToolbarActionService {
  private readonly _emitter = new TypedEmitter<ToolbarActionServiceEvents>();
  private _actions: readonly ToolbarAction[] = [];
  private _activePanel: ActionPanelState = null;
  private _infoPaneTopicId: InfoPaneTopicId | undefined = undefined;
  private _infoPaneCurrentPage: ContentNode | undefined = undefined;
  private _nextRegistrationId = 1;
  private _currentRegistrationId = 0;

  /** Currently registered actions, sorted by sortOrder then registration order. */
  get actions(): readonly ToolbarAction[] {
    return this._actions;
  }

  /** Returns the current active panel ID, or null if no getter is registered or panel is closed. */
  get activePanel(): ActionPanelState {
    return this._activePanel;
  }

  set activePanel(value: ActionPanelState) {
    this._activePanel = value;
    this._emitter.emit('changed');
  }

  /** The topic currently displayed in the info pane, or undefined if closed. */
  get infoPaneTopicId(): InfoPaneTopicId | undefined {
    return this._infoPaneTopicId;
  }

  /** The content page the user is viewing when the info pane was opened. */
  get infoPaneCurrentPage(): ContentNode | undefined {
    return this._infoPaneCurrentPage;
  }

  /** Opens the info pane to the given topic for the specified content page. */
  changeInfoPaneTopic(topicId: InfoPaneTopicId, currentPage: ContentNode | undefined): void {
    this._infoPaneTopicId = topicId;
    this._infoPaneCurrentPage = currentPage;
    this._emitter.emit('changed');
  }

  /** Clears the info pane topic and current page. */
  clearInfoPaneTopic(): void {
    this._infoPaneTopicId = undefined;
    this._infoPaneCurrentPage = undefined;
    this._emitter.emit('changed');
  }

  /**
   * Register toolbar actions. Replaces any previously registered actions.
   * @param actions - 1 to 10 ToolbarAction descriptors
   * @returns A registration ID that can be passed to tryDeregisterActions.
   * @throws Error if validation fails (empty array, >10 actions, empty IDs, invalid tooltips, duplicate IDs)
   */
  registerActions(actions: readonly ToolbarAction[]): number {
    this.validateActions(actions);
    this._actions = this.sortActions(actions);
    // Default to the first enabled action; a disabled panel must not auto-open
    // (e.g. the not-found page disables every action, so no panel opens).
    this._activePanel = this.actions.find((action) => !action.disabled)?.id ?? null;
    const registrationId = this._nextRegistrationId++;
    this._currentRegistrationId = registrationId;
    this._emitter.emit('changed');
    return registrationId;
  }

  /** Clears actions only if the given registrationId matches the current one. */
  tryDeregisterActions(registrationId: number): void {
    if (registrationId !== this._currentRegistrationId) return;
    this._actions = [];
    this._activePanel = null;
    this._currentRegistrationId = 0;
    this._emitter.emit('changed');
  }

  /** Subscribe to service events. Returns an unsubscribe function. */
  on<K extends keyof ToolbarActionServiceEvents>(
    event: K,
    listener: ToolbarActionServiceEvents[K],
  ): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  private validateActions(actions: readonly ToolbarAction[]): void {
    if (actions.length === 0) {
      return; // Empty array clears actions; registerActions still updates callbacks.
    }

    if (actions.length > 10) {
      throw new Error(`Cannot register more than 10 toolbar actions. Received ${actions.length}.`);
    }

    const seenIds = new Set<string>();

    for (const action of actions) {
      if (!action.id || action.id.length === 0) {
        throw new Error('Toolbar action ID must be a non-empty string.');
      }

      if (action.id.length > 64) {
        throw new Error(
          `Toolbar action ID must be at most 64 characters. Got "${action.id}" (${action.id.length} chars).`,
        );
      }

      if (!action.tooltip || action.tooltip.length > 128) {
        throw new Error(
          `Toolbar action tooltip must be between 1 and 128 characters. Got "${action.tooltip}" (${action.tooltip?.length ?? 0} chars) for action "${action.id}".`,
        );
      }

      if (action.ariaLabel !== undefined && (!action.ariaLabel || action.ariaLabel.length > 128)) {
        throw new Error(
          `Toolbar action ariaLabel must be between 1 and 128 characters if provided. Got "${action.ariaLabel}" (${action.ariaLabel?.length ?? 0} chars) for action "${action.id}".`,
        );
      }

      if ((action.icon === undefined) === (action.iconSvg === undefined)) {
        throw new Error(
          `Toolbar action must specify exactly one of "icon" or "iconSvg" for action "${action.id}".`,
        );
      }

      if (seenIds.has(action.id)) {
        throw new Error(`Duplicate toolbar action ID: "${action.id}".`);
      }
      seenIds.add(action.id);
    }
  }

  private sortActions(actions: readonly ToolbarAction[]): readonly ToolbarAction[] {
    // Stable sort: explicit sortOrder ascending, then registration order for ties/missing.
    // Array.prototype.sort is stable in modern JS engines (ES2019+).
    return [...actions].sort((a, b) => {
      const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
  }
}
